import {
  Box,
  Flex,
  Heading,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { useMemo } from "react";

const TAB_KEYS = ["billing", "memberships", "credits", "methods"] as const;
type TabKey = (typeof TAB_KEYS)[number];

const TAB_LABEL: Record<TabKey, string> = {
  billing: "Billing",
  memberships: "Memberships",
  credits: "Credits & gift cards",
  methods: "Payment methods",
};

type Props = {
  children: React.ReactNode;
  active: TabKey;
  headerExtras?: React.ReactNode;
  /** Tabs this clinic has nothing to show in (a clinic that sells no plans). */
  hidden?: readonly TabKey[];
};

export const AccountLayout = ({ children, active, headerExtras, hidden = [] }: Props) => {
  const navigate = useNavigate();
  const keys = useMemo(() => TAB_KEYS.filter((key) => !hidden.includes(key)), [hidden]);
  const activeIndex = useMemo(() => keys.indexOf(active), [keys, active]);
  return (
    <Box px={{ base: 4, md: 6 }} pt={5} pb={4} flex={1} overflow="auto">
      <Flex mb={4} justify="space-between" align={{ base: "flex-start", md: "center" }} direction={{ base: "column", md: "row" }} gap={3}>
        <Box>
          <Heading as="h2" fontSize="xl" color="#1a1f36" fontWeight="600">
            Account
          </Heading>
          <Text fontSize="14px" color="#6b7280" mt={1}>
            Your financial activity, memberships, and payment methods.
          </Text>
        </Box>
        {headerExtras}
      </Flex>
      <Tabs
        index={activeIndex < 0 ? 0 : activeIndex}
        onChange={(idx) => {
          const key = keys[idx];
          if (key === "billing") navigate("/account");
          else navigate(`/account/${key}`);
        }}
        colorScheme="blue"
        mb={4}
        isLazy
        variant="line"
      >
        <TabList borderBottomColor="#e6e6e6">
          {keys.map((key) => (
            <Tab
              key={key}
              fontSize="14px"
              fontWeight="500"
              color="#6b7280"
              _selected={{ color: "#1a1f36", borderColor: "#1a1f36" }}
            >
              {TAB_LABEL[key]}
            </Tab>
          ))}
        </TabList>
        <TabPanels>
          {keys.map((key) => (
            <TabPanel key={key} px={0} pt={5}>
              {key === active ? children : null}
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
    </Box>
  );
};
